import { ProviderTypes } from "lib/generated/integration-api";

/**
 * The CRMs that exist only behind Paragon. Each connects through the Connect Portal and builds its
 * send form from the field mapping rather than from anything we name, so none of the copy, setup
 * pages or server-built forms keyed by provider type reaches one.
 */
export type ParagonOnlyProviderType = typeof ProviderTypes.Dynamics | typeof ProviderTypes.Zoho;

/**
 * The CRMs this file has copy, icons and a setup page for — everything keyed by provider type here.
 */
export type NativeProviderType = Exclude<ProviderTypes, ParagonOnlyProviderType>;

/**
 * Takes a plain string so it can screen values from Paragon and from the API alike, and narrows on
 * the way out, so a caller that has ruled these out is left with the providers it can render.
 */
export function isParagonOnlyProvider(
  providerType: string | undefined,
): providerType is ParagonOnlyProviderType {
  return providerType === ProviderTypes.Dynamics || providerType === ProviderTypes.Zoho;
}

export type ProviderMetadata = {
  name: string;
  description: string;
  learnMoreModal: {
    tagline: string;
    adminMessage: JSX.Element;
    contentBlocks: {
      subtitle: string;
      text: string;
    }[];
    setupLink: string;
  };
  createNoticeModal: {
    [entityName: string]: { title: string; subtitle: string };
  };
};

export type ProviderOpportunityMetadata = {
  name: string;
  description: string;
  learnMoreModal: {
    tagline: string;
    adminMessage: JSX.Element;
    contentBlocks: {
      subtitle: string;
      text: string;
    }[];
    setupLink: string;
  };
  createOpportunityModal: {
    [entityName: string]: { title: string; subtitle: string };
  };
};

export const PROVIDER_METADATA: Record<NativeProviderType, ProviderMetadata> = {
  HUBSPOT: {
    name: "HubSpot",
    description:
      "Connect to your Hubspot account to send relevant notices direct to your workflow.",
    learnMoreModal: {
      tagline: "Instantly create objects in your CRM",
      adminMessage: (
        <>
          <p>
            In order to setup this integration, you will need to be <b>Super Admin on HubSpot</b>.
            See how you can view HubSpot Super Admin permissions in our guide{" "}
            <a
              target="_blank"
              href="https://www.notion.so/HubSpot-Administration-Guide-d58c0cc3ab1947d39c1ae821a873be44"
              rel="noreferrer"
            >
              here
            </a>
            .{" "}
          </p>
          <p>
            Add your CRM admins to your Stotles account{" "}
            <a
              target="_blank"
              href="https://app.stotles.com/account-management/my-team"
              rel="noreferrer"
            >
              here
            </a>{" "}
            if needed.
          </p>
        </>
      ),

      setupLink: "https://help.stotles.com/set-up-hubspot",
      contentBlocks: [
        {
          subtitle: "Shortcut your workflow",
          text: "Cut out the unnecessary processes that go between Stotles' insights and HubSpot. Instead, store relevant deals and contacts to your CRM in a few simple clicks.",
        },
        {
          subtitle: "Enhance team productivity",
          text: "Not only does this integration save time, but ensures your team members have immediate access to the information they need to better serve your customers.",
        },
        {
          subtitle: "Keep track of where your successes come from",
          text: "Showing ROI is always crucial for the tools you love. By seamlessly linking back to the source of an deal, you can show exactly where your top leads are coming from without any extra input.",
        },
      ],
    },
    createNoticeModal: {
      Deal: {
        title: "Create a deal in HubSpot",
        subtitle: "Add this notice to your deals in HubSpot.",
      },
    },
  },
  SALESFORCE: {
    name: "Salesforce",
    description:
      "Connect to your Salesforce account to send relevant notices direct to your workflow.",
    learnMoreModal: {
      tagline: "Instantly create objects in your CRM",
      adminMessage: (
        <>
          <p>
            In order to setup this integration, you will need to be{" "}
            <b>System Administrator on Salesforce</b>. See how you can view Salesforce System
            Administrator permissions in our guide{" "}
            <a
              target="_blank"
              href="https://stotles.notion.site/Salesforce-Administration-Guide-9405b2d3017c458d9109f98985288db9"
              rel="noreferrer"
            >
              here.
            </a>{" "}
          </p>
          <p>
            Add your CRM admins to your Stotles account{" "}
            <a
              target="_blank"
              href="https://app.stotles.com/account-management/my-team"
              rel="noreferrer"
            >
              here
            </a>{" "}
            if needed.
          </p>
        </>
      ),
      setupLink: "https://help.stotles.com/setting-up-salesforce",
      contentBlocks: [
        {
          subtitle: "Shortcut your workflow",
          text: "Cut out the unnecessary processes that go between Stotles' insights and Salesforce. Instead, store relevant opportunities and contacts to your CRM in a few simple clicks.",
        },
        {
          subtitle: "Enhance team productivity",
          text: "Not only does this integration save time, but ensures your team members have immediate access to the information they need to better serve your customers.",
        },
        {
          subtitle: "Keep track of where your successes come from",
          text: "Showing ROI is always crucial for the tools you love. By seamlessly linking back to the source of an deal, you can show exactly where your top leads are coming from without any extra input.",
        },
      ],
    },
    createNoticeModal: {
      Lead: {
        title: "Create a lead in Salesforce",
        subtitle: "Add this notice to your leads in Salesforce.",
      },
      Opportunity: {
        title: "Create an opportunity in Salesforce",
        subtitle: "Add this notice to your opportunities in Salesforce.",
      },
    },
  },
};

export const PROVIDER_OPPORTUNITY_METADATA: Record<
  NativeProviderType,
  ProviderOpportunityMetadata
> = {
  HUBSPOT: {
    name: "HubSpot",
    description:
      "Connect to your HubSpot account to send relevant opportunities direct to your workflow.",
    learnMoreModal: {
      tagline: "Instantly create objects in your CRM",
      adminMessage: (
        <>
          <p>
            In order to setup this integration, you will need to be <b>Super Admin on HubSpot</b>.
            See how you can view HubSpot Super Admin permissions in our guide{" "}
            <a
              target="_blank"
              href="https://www.notion.so/HubSpot-Administration-Guide-d58c0cc3ab1947d39c1ae821a873be44"
              rel="noreferrer"
            >
              here
            </a>
            .{" "}
          </p>
          <p>
            Add your CRM admins to your Stotles account{" "}
            <a
              target="_blank"
              href="https://app.stotles.com/account-management/my-team"
              rel="noreferrer"
            >
              here
            </a>{" "}
            if needed.
          </p>
        </>
      ),

      setupLink: "https://help.stotles.com/set-up-hubspot",
      contentBlocks: [
        {
          subtitle: "Shortcut your workflow",
          text: "Cut out the unnecessary processes that go between Stotles' insights and HubSpot. Instead, store relevant deals and contacts to your CRM in a few simple clicks.",
        },
        {
          subtitle: "Enhance team productivity",
          text: "Not only does this integration save time, but ensures your team members have immediate access to the information they need to better serve your customers.",
        },
        {
          subtitle: "Keep track of where your successes come from",
          text: "Showing ROI is always crucial for the tools you love. By seamlessly linking back to the source of an deal, you can show exactly where your top leads are coming from without any extra input.",
        },
      ],
    },
    createOpportunityModal: {
      Deal: {
        title: "Create a deal in HubSpot",
        subtitle: "Use this Opportunity to populate a deal in HubSpot",
      },
    },
  },
  SALESFORCE: {
    name: "Salesforce",
    description:
      "Connect to your Salesforce account to send relevant opportunities direct to your workflow.",
    learnMoreModal: {
      tagline: "Instantly create objects in your CRM",
      adminMessage: (
        <>
          <p>
            In order to setup this integration, you will need to be{" "}
            <b>System Administrator on Salesforce</b>. See how you can view Salesforce System
            Administrator permissions in our guide{" "}
            <a
              target="_blank"
              href="https://stotles.notion.site/Salesforce-Administration-Guide-9405b2d3017c458d9109f98985288db9"
              rel="noreferrer"
            >
              here.
            </a>{" "}
          </p>
          <p>
            Add your CRM admins to your Stotles account{" "}
            <a
              target="_blank"
              href="https://app.stotles.com/account-management/my-team"
              rel="noreferrer"
            >
              here
            </a>{" "}
            if needed.
          </p>
        </>
      ),
      setupLink: "https://help.stotles.com/setting-up-salesforce",
      contentBlocks: [
        {
          subtitle: "Shortcut your workflow",
          text: "Cut out the unnecessary processes that go between Stotles' insights and Salesforce. Instead, store relevant opportunities and contacts to your CRM in a few simple clicks.",
        },
        {
          subtitle: "Enhance team productivity",
          text: "Not only does this integration save time, but ensures your team members have immediate access to the information they need to better serve your customers.",
        },
        {
          subtitle: "Keep track of where your successes come from",
          text: "Showing ROI is always crucial for the tools you love. By seamlessly linking back to the source of an deal, you can show exactly where your top leads are coming from without any extra input.",
        },
      ],
    },
    createOpportunityModal: {
      Lead: {
        title: "Create a lead in Salesforce",
        subtitle: "Use this opportunity to populate a Lead in Salesforce",
      },
      Opportunity: {
        title: "Create an Opportunity in Salesforce",
        subtitle: "Use this opportunity to populate an Opportunity in Salesforce",
      },
    },
  },
};
